<script lang="ts">
	import { devices_settings } from 'src/stores/device-runtime/status';
	//Formatters
	import { format } from 'svelte-i18n';
	//Helpers
	import { real_id } from '../../../helpers/interface_helpers';
	import { onSettingsResponse } from '../../../helpers/device_settings_helpers';
	import { capFirstChar, openExternalLink } from '../../../helpers/utilities';
	//Services
	import { gen2Request } from '../../../services/gen_2_http_reqs';
	//Stores
	import {
		getDeviceSettings,
		getDeviceStatus,
		readDeviceSettings,
	} from 'src/stores/device-runtime/status-selectors';
	//Design system components
	import {
		ToggleV2,
		Checkbox,
		Select,
		SelectV2,
		Input,
		InputV2,
		Button,
		Text,
		Alert,
		Icon,
	} from '@Shelly/shelly-design-system';
	//Components
	import NetworkConnectedStatus from '../NetworkComponents/NetworkConnectedStatus.svelte';
	import NetworkModal from '../NetworkComponents/NetworkModal.svelte';
	//Types
	import type { SettingRequest } from '../../../types/requests/settings/setting';
	import type { Device } from '../../../types/Device';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { getContext, onDestroy } from 'svelte';
	import type { Writable } from 'svelte/store';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';
	import { navigateToSetting } from 'src/stores/ui/navigate-to';
	import SettingGuide from '../SettingGuide.svelte';

	export let device: Device;
	export let isOffline;

	const id = real_id(device.id),
		settings = readDeviceSettings(id),
		options = ['*', 'ca.pem', 'user_ca.pem']; //removing 'No TTS/SSL', because null is not supported

	$: statusStore = getDeviceStatus(id);

	let enable = settings?.ws?.enable,
		server = settings?.ws?.server,
		ssl_ca = settings?.ws?.ssl_ca,
		showModal = false,
		waitForResponse = false;

	let moreInfoStr = `${$format('about_outbound_txt', {
		default: 'About Outbound Server',
	})}`;
	let splittedMoreInfStr = moreInfoStr.split(' ');

	const save = async () => {
		waitForResponse = true;
		const params = {
				config: {
					enable,
					server,
					ssl_ca,
				},
			},
			res: SettingRequest = await gen2Request(device, 'Ws.SetConfig', params);
		if (res.isok) {
			settings.wifi.ws = params.config;
			devices_settings.update_devices_settings(id, settings);
		}
		onSettingsResponse(res, device, {
			success: enable
				? $format('outbound_scc_enabled_toast', {
						default: 'Outbound server is enabled.',
					})
				: $format('outbound_scc_disabled_toast', {
						default: 'Outbound server is disabled.',
					}),
		});
		if ($showNewLayout) {
			navigateToSetting(device.id, 'networks');
		}
		waitForResponse = false;
	};

	const onToggleChange = () => {
		enable = !enable;
		if ($showNewLayout && settings?.ws?.enable && !enable) {
			showModal = true;
		}
	};

	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	$: if (stickyBottomAction) {
		if ($showNewLayout && enable) {
			stickyBottomAction.set({
				onClick: () => (showModal = true),
				loading: waitForResponse,
				disabled: isOffline,
			});
		} else {
			stickyBottomAction.set(null);
		}
	}

	onDestroy(() => {
		stickyBottomAction?.set(null);
	});
</script>

<SettingGuide
	text={$format('settings_guide_outbound_websocket', {
		default:
			'Keep a live connection to a remote server so this device can push status and events as they happen. Advanced — most homes do not need this.',
	})}
/>

{#if $showNewLayout}
	<ToggleV2
		size="m"
		class="mb-xs"
		checked={enable}
		on:click={onToggleChange}
		disabled={isOffline}
		id="checkbox-pfas4f8"
	>
		{capFirstChar(
			$format('sub_settings_title_outbound_websocket', { default: 'outbound websocket' }),
		)}
	</ToggleV2>
	{#if enable}
		<NetworkConnectedStatus connected={$statusStore?.ws?.connected} />

		<Alert
			type="warning"
			isHeadingBold
			heading={$format('dev_reboot_required_label', { default: 'Device reboot required' })}
			class="mb-s-plus"
			message={$format('mqtt_warning_msg', {
				default: 'Changing these settings requires a device reboot!',
			})}
		/>

		<SelectV2
			aria-label={$format('select_ssl_certificate', {
				default: 'Select SSL certificate',
			})}
			label={$format('select_ssl_certificate', {
				default: 'Select SSL certificate',
			})}
			size="m"
			class="mb-s"
			bind:value={ssl_ca}
			{options}
			icon="fas fa-file-certificate"
			disabled={!enable || isOffline}
			id="select-vtfm4ns"
		/>

		<InputV2
			size="m"
			label="Server address i.e. ws://my.server.com"
			bind:value={server}
			icon="far fa-server"
			autocomplete="nope"
			disabled={!enable || isOffline}
			id="input-s02dfk9"
			aria-labelledby="input-s02dfk9"
		/>
	{/if}
{:else}
	<Checkbox size="l" class="mb-s" bind:checked={enable} disabled={isOffline} id="checkbox-pfas4f8">
		{$format('button_enable', { default: 'Enable' })}
	</Checkbox>

	<hr class="mb-m" />

	<NetworkConnectedStatus connected={$statusStore?.ws?.connected} />

	<Alert
		type="warning"
		isHeadingBold
		heading={$format('label_info')}
		class="mb-s"
		message={$format('mqtt_warning', {
			default: 'WARNING: Changing these settings requires a device reboot!',
		})}
	/>

	<Select
		aria-label={$format('select_ssl_certificate', {
			default: 'Select SSL certificate',
		})}
		size="m"
		class="mb-s"
		bind:value={ssl_ca}
		{options}
		icon="fas fa-file-certificate"
		disabled={!enable || isOffline}
		id="select-vtfm4ns"
	/>

	<Input
		size="m"
		class="mb-s"
		label="Server address i.e. ws://my.server.com"
		bind:value={server}
		icon="far fa-server"
		autocomplete="nope"
		disabled={!enable || isOffline}
		id="input-s02dfk9"
		aria-labelledby="input-s02dfk9"
	/>

	<Button
		disabled={isOffline}
		size="m"
		width="full"
		on:click={() => (showModal = true)}
		loading={waitForResponse}>{$format('button_save', { default: 'Save' })}</Button
	>
{/if}

<NetworkModal
	bind:showModal
	on:confirm={save}
	{isOffline}
	btnTexts={{
		cancelText: '',
		saveText: enable
			? $format('enable', { default: 'Enable' })
			: $format('button_disable', { default: 'Disable' }),
	}}
	title={enable
		? $format('enable_outbound_server_title', {
				default: 'Enable Outbound Server?',
			})
		: $format('disable_outbound_server_title', {
				default: 'Disable Outbound Server?',
			})}
>
	{$format('enable_disable_outbound_msg', {
		values: {
			enable_disable: enable ? $format('option_enable') : $format('option_disable'),
			default: `Are you sure you want to {enable_disable} outbound server for this device?`,
		},
	})}
	<div class="d-flex align-items-center mt-m" slot="additionalInfo">
		<Text styleType="body-small">
			{$format('need_more_inf_txt', { default: 'Need more info?' })}
			<Text
				target="_blank"
				class="link-styles link-underline  d-inline"
				styleType="body-small"
				on:click={() => {
					openExternalLink(
						'https://shelly-api-docs.shelly.cloud/gen2/ComponentsAndServices/Ws/',
					);
				}}
			>
				<span class="d-inline-block">
					<Icon
						size="s"
						class="text-primary"
						icon="fa-regular fa-arrow-up-right-from-square"
					/>
					{splittedMoreInfStr[0]}
				</span>
				{splittedMoreInfStr.slice(1).join(' ')}
			</Text>
		</Text>
	</div>
</NetworkModal>
